<script lang="ts">
    import { onMount } from 'svelte';
    import { fetchFuturesInfo, type FuturesInfo, type IndicatorPoint } from '../api/klines';
    import { tooltip } from '../actions/tooltip';

    let timelines: string[] = $state(['15m', '30m', '1h', '4h', '1d', '1w']);

    interface Params {
        fetchData: () => void;
        coinIntervals: Record<string, string>;
        coin: string;
        openChartInModal: (coin: string) => void;
        lastIndicator?: IndicatorPoint | null;
    }
    let { fetchData, coinIntervals = $bindable(), coin, openChartInModal, lastIndicator }: Params = $props();

    let futuresInfo = $state<FuturesInfo | null>(null);

    async function loadFuturesInfo() {
        futuresInfo = await fetchFuturesInfo(coin);
    }

    onMount(() => {
        loadFuturesInfo();
        const interval = setInterval(loadFuturesInfo, 30_000);
        return () => clearInterval(interval);
    });

    $effect(() => {
        void coin;
        loadFuturesInfo();
    });

    function setCoinInterval(coin: string, interval: string) {
        coinIntervals[coin] = interval;
        localStorage.setItem('coinIntervals', JSON.stringify(coinIntervals));
        fetchData();
    }

    function rsiColor(rsi: number): string {
        if (rsi >= 70) return '#c62828';
        if (rsi <= 30) return '#2e7d32';
        return '#546e7a';
    }

    function fundingColor(rate: number): string {
        if (rate > 0.0005) return '#c62828';
        if (rate < -0.0001) return '#2e7d32';
        return '#546e7a';
    }

    function rsiTooltip(rsi: number): string {
        const v = Math.round(rsi);
        if (rsi >= 80) return `RSI ${v} — сильно перекуплений ⚠\nРизик різкого падіння.\nНе відкривай лонг. Шукай ведмежі патерни для шорту.`;
        if (rsi >= 70) return `RSI ${v} — перекуплений ⚠\nМожливий відкат вниз.\nНові лонги небезпечні. Розглядай шорт з підтвердженням.`;
        if (rsi <= 20) return `RSI ${v} — сильно перепроданий ✓\nРизик різкого відскоку вгору.\nШукай бичачі патерни для лонгу.`;
        if (rsi <= 30) return `RSI ${v} — перепроданий ✓\nМожливий відскок вгору.\nРозглядай лонг з підтвердженням патерну.`;
        if (rsi >= 55) return `RSI ${v} — нейтрально-бичачий\nТренд зростає, але без чіткого сигналу.\nТримай наявні лонги, нових не відкривай.`;
        if (rsi <= 45) return `RSI ${v} — нейтрально-ведмежий\nТренд падає, але без чіткого сигналу.\nТримай наявні шорти, нових не відкривай.`;
        return `RSI ${v} — нейтральна зона (45–55)\nРинок у рівновазі, немає сигналу.\nОчікуй виходу RSI за межі або чіткого патерну.`;
    }

    function atrTooltip(atr: number): string {
        return `ATR ${atr.toPrecision(3)} — середня волатильність\nза останні 14 свічок.\n\nСтоп-лос:    ціна входу ± ATR × 1.5\nТейк-профіт: ціна входу ± ATR × 3\n\nБільший ATR → ширший стоп,\nменший розмір позиції.`;
    }

    function frTooltip(rate: number, pct: number): string {
        const s = pct > 0 ? `+${pct}` : `${pct}`;
        if (rate > 0.001)  return `FR ${s}% — екстремально багато лонгів ⚠\nЛонгери платять шортерам кожні 8г.\nРинок дуже перегрітий. Уникай лонгу.`;
        if (rate > 0.0005) return `FR ${s}% — переважають лонги\nЛонгери платять шортерам.\nОбережно з відкриттям нових лонгів.`;
        if (rate < -0.0003) return `FR ${s}% — дуже багато шортів ✓\nШортери платять лонгерам.\nМожливий short squeeze вгору.`;
        if (rate < -0.0001) return `FR ${s}% — переважають шорти\nШортери платять лонгерам.\nОбережно з новими шортами.`;
        return `FR ${s}% — нейтральний funding\nБаланс між лонгами та шортами.\nНемає перекосу — нейтрально.`;
    }

    function lsTooltip(ls: number): string {
        const v = ls.toFixed(2);
        if (ls > 2.5) return `L/S ${v} — більшість у лонгах ⚠\nНатовп часто помиляється.\nРизик розвороту вниз. Обережно з лонгом.`;
        if (ls > 1.5) return `L/S ${v} — переважають лонги\nНевеликий перекіс до биків.\nНейтрально, стежи за FR.`;
        if (ls < 0.5) return `L/S ${v} — більшість у шортах ✓\nМожливий short squeeze вгору.\nРизик різкого зросту.`;
        if (ls < 0.7) return `L/S ${v} — переважають шорти\nНевеликий перекіс до ведмедів.\nНейтрально, стежи за FR.`;
        return `L/S ${v} — збалансоване співвідношення\nПриблизно рівна кількість лонгів і шортів.\nНейтральний сигнал.`;
    }


</script>

<div class="toolbar d-flex flex-row align-items-center justify-content-between flex-wrap gap-1">
    <!-- Таймфрейми -->
    <div class="d-flex flex-row flex-wrap gap-1">
        {#each timelines as time}
            <button
                class="btn btn-sm btn-outline-secondary p-1 lh-1"
                class:active={coinIntervals[coin] === time}
                onclick={() => setCoinInterval(coin, time)}
            >
                {time}
            </button>
        {/each}
    </div>

    <!-- Індикатори -->
    <div class="d-flex flex-row align-items-center gap-2">
        {#if lastIndicator?.rsi != null}
            {@const rsi = Math.round(lastIndicator.rsi)}
            {@const rsiVal = lastIndicator.rsi}
            <span
                class="badge"
                style="background:{rsiColor(rsiVal)};font-size:0.7rem;cursor:default"
                use:tooltip={() => rsiTooltip(rsiVal)}
            >
                RSI {rsi}{rsi >= 70 ? ' ⚠' : rsi <= 30 ? ' ✓' : ''}
            </span>
        {/if}

        {#if lastIndicator?.atr != null}
            {@const atrVal = lastIndicator.atr}
            <span
                class="text-muted"
                style="font-size:0.7rem;cursor:default"
                use:tooltip={() => atrTooltip(atrVal)}
            >
                ATR {atrVal.toPrecision(3)}
            </span>
        {/if}

        {#if futuresInfo?.success}
            {@const pct  = futuresInfo.fundingRatePct}
            {@const rate = futuresInfo.fundingRate}
            <span
                class="badge"
                style="background:{fundingColor(rate)};font-size:0.7rem;cursor:default"
                use:tooltip={() => frTooltip(rate, pct)}
            >
                FR {pct > 0 ? '+' : ''}{pct}%
            </span>

            {#if futuresInfo.longShortRatio != null}
                {@const ls = futuresInfo.longShortRatio}
                <span
                    class="text-muted"
                    style="font-size:0.7rem;cursor:default"
                    use:tooltip={() => lsTooltip(ls)}
                >
                    L/S {ls.toFixed(2)}
                </span>
            {/if}
        {/if}

        <!-- Кнопка модалки -->
        <button class="btn btn-sm btn-outline-secondary p-1 lh-1" onclick={() => openChartInModal(coin)}>
            <svg width="18px" height="14px" viewBox="0 0 122.88 97.52">
                <path fill="#6c757d" fill-rule="evenodd" d="M61.44,28.68A20.08,20.08,0,1,1,41.36,48.76,20.08,20.08,0,0,1,61.44,28.68ZM22.3,91.37a3.09,3.09,0,0,1,3.08,3.08,3.07,3.07,0,0,1-3.08,3.07H3.87A3.07,3.07,0,0,1,.8,94.45V76.3a3.08,3.08,0,0,1,5.25-2.17l.07.08A3.08,3.08,0,0,1,7,76.3V91.37ZM22.3,0a3.11,3.11,0,0,1,2.18.9l.07.08A3.07,3.07,0,0,1,22.3,6.15H7V21.22a3.08,3.08,0,0,1-6.15,0V3.08A3,3,0,0,1,1.7.91,3,3,0,0,1,3.87,0Zm78.28,6.15A3.07,3.07,0,0,1,97.5,3.08,3.09,3.09,0,0,1,100.58,0H119a3.1,3.1,0,0,1,2.17.9,3.07,3.07,0,0,1,.9,2.18V21.22A3.07,3.07,0,0,1,119,24.29a3.09,3.09,0,0,1-3.08-3.07V6.15Zm0,91.37a3.07,3.07,0,0,1-3.08-3.07,3.09,3.09,0,0,1,3.08-3.08h15.35V76.3A3.07,3.07,0,0,1,119,73.23a3.07,3.07,0,0,1,3.07,3.07V94.45A3.07,3.07,0,0,1,119,97.52ZM1,46.24a94.65,94.65,0,0,1,9.15-9.63c14.34-13,31.36-20,48.91-20.37s35.47,5.9,51.7,19.29A108.5,108.5,0,0,1,122,46.27a3.56,3.56,0,0,1,.23,4.51,73.18,73.18,0,0,1-14.07,15A71.92,71.92,0,0,1,63.35,81.29a79,79,0,0,1-45.8-14.57A82.23,82.23,0,0,1,.77,50.87,3.55,3.55,0,0,1,1,46.24Zm13.94-4.38a86.45,86.45,0,0,0-6.7,6.82A74.74,74.74,0,0,0,21.64,60.91,72,72,0,0,0,63.33,74.2a64.88,64.88,0,0,0,40.37-14,65.76,65.76,0,0,0,11-11.25A100.31,100.31,0,0,0,106.22,41C91.33,28.74,75,23,59.15,23.33S27.93,30,14.89,41.86Zm46.55-.44a7.35,7.35,0,1,1-7.35,7.34,7.34,7.34,0,0,1,7.35-7.34Z"/>
            </svg>
        </button>
    </div>
</div>

<style>
    button:hover svg path { fill: #fff; }

    /* Глобальний стиль тултіпа — додається через document.body */
    :global(.__ind-tooltip) {
        position: fixed;
        background: #1e1e1e;
        color: #f0f0f0;
        padding: 9px 13px;
        border-radius: 7px;
        font-size: 0.76rem;
        width: 240px;
        z-index: 99999;
        pointer-events: none;
        line-height: 1.6;
        white-space: pre-line;
        box-shadow: 0 4px 20px rgba(0,0,0,0.3);
    }
</style>
